import {
  Chip,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useState } from 'react';

import { searchTransferItems } from '../../api/business/endpoints';
import type {
  TransferItemMatch,
  TransferItemOutcome,
  TransferItemSortKey,
  TransferMode,
} from '../../api/business/types';
import { EmptyState } from '../../components/EmptyState';
import { ErrorNotice } from '../../components/ErrorNotice';
import { ServerPagination } from '../../components/ServerPagination';
import { SortableCell } from '../../components/SortableCell';
import { TableFilters } from '../../components/TableFilters';
import { TableSkeletonRows } from '../../components/TableSkeletonRows';
import { outcomeReasonKey } from '../../components/transfers';
import { useDebouncedValue } from '../../components/useDebouncedValue';
import { useSort } from '../../components/useSort';
import { useT } from '../../i18n/LanguageContext';

const MATCHES: Array<TransferItemMatch> = ['NEW', 'MATCHED', 'INVALID'];
const OUTCOMES: Array<TransferItemOutcome> = ['CREATED', 'UPDATED', 'SKIPPED', 'FAILED'];

/**
 * The products of an import's package (R0037): before it is applied what each would be — new, matched to one of the
 * company's (and so, by the mode chosen, skipped, filled or overwritten), refused; afterwards what the import did.
 * Server-paginated, filtered and sorted.
 */
export const TransferItemsTable = ({
  companyId,
  transferId,
  applied,
  mode,
}: {
  companyId: string;
  transferId: string;
  applied: boolean;
  mode: TransferMode;
}) => {
  const t = useT();
  const [match, setMatch] = useState<TransferItemMatch | ''>('');
  const [outcome, setOutcome] = useState<TransferItemOutcome | ''>('');
  const [term, setTerm] = useState('');
  const debouncedTerm = useDebouncedValue(term);
  const [page, setPage] = useState(0);
  const sort = useSort<TransferItemSortKey>('position', 'asc', () => setPage(0));
  const query = useQuery({
    queryKey: [
      'transfer-items',
      companyId,
      transferId,
      match,
      outcome,
      debouncedTerm,
      page,
      sort.key,
      sort.direction,
    ],
    queryFn: () =>
      searchTransferItems(companyId, transferId, {
        match: match || undefined,
        outcome: outcome || undefined,
        search: debouncedTerm.trim() || undefined,
        page: page + 1,
        sort: sort.key,
        order: sort.direction,
      }),
    placeholderData: keepPreviousData,
  });
  const rows = query.data?.items ?? [];

  return (
    <Paper variant="outlined">
      <ErrorNotice error={query.error} onRetry={() => void query.refetch()} />
      <TableFilters>
        <TextField
          size="small"
          label={t('transfers.itemSearch')}
          value={term}
          onChange={(event) => {
            setTerm(event.target.value);
            setPage(0);
          }}
          sx={{ minWidth: 240 }}
        />
        <TextField
          select
          size="small"
          label={t('transfers.match')}
          value={match}
          onChange={(event) => {
            setMatch(event.target.value as TransferItemMatch | '');
            setPage(0);
          }}
          sx={{ minWidth: 170 }}
        >
          <MenuItem value="">{t('common.all')}</MenuItem>
          {MATCHES.map((one) => (
            <MenuItem key={one} value={one}>
              {t(`transferMatch.${one}` as const)}
            </MenuItem>
          ))}
        </TextField>
        {applied ? (
          <TextField
            select
            size="small"
            label={t('transfers.outcome')}
            value={outcome}
            onChange={(event) => {
              setOutcome(event.target.value as TransferItemOutcome | '');
              setPage(0);
            }}
            sx={{ minWidth: 170 }}
          >
            <MenuItem value="">{t('common.all')}</MenuItem>
            {OUTCOMES.map((one) => (
              <MenuItem key={one} value={one}>
                {t(`transferOutcome.${one}` as const)}
              </MenuItem>
            ))}
          </TextField>
        ) : null}
      </TableFilters>
      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              <SortableCell sort={sort} column="position">
                #
              </SortableCell>
              <SortableCell sort={sort} column="name">
                {t('transfers.product')}
              </SortableCell>
              <SortableCell sort={sort} column="sku">
                SKU
              </SortableCell>
              <SortableCell sort={sort} column="ean">
                EAN
              </SortableCell>
              <SortableCell sort={sort} column="match">
                {t('transfers.match')}
              </SortableCell>
              <SortableCell sort={sort} column="outcome">
                {applied ? t('transfers.outcome') : t('transfers.wouldDo')}
              </SortableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {query.isLoading ? (
              <TableSkeletonRows columns={6} />
            ) : (
              rows.map((item) => (
                <TableRow key={item.id}>
                  <TableCell>{item.position + 1}</TableCell>
                  <TableCell>
                    {item.name}
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                      {t('transfers.itemShape', { variants: item.variants, photos: item.photos })}
                    </Typography>
                  </TableCell>
                  <TableCell sx={{ fontFamily: 'ui-monospace, monospace', fontSize: 13 }}>
                    {item.sku ?? t('common.none')}
                  </TableCell>
                  <TableCell sx={{ fontFamily: 'ui-monospace, monospace', fontSize: 13 }}>
                    {item.ean ?? t('common.none')}
                  </TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      variant="outlined"
                      color={item.match === 'INVALID' ? 'error' : item.match === 'NEW' ? 'success' : 'info'}
                      label={
                        item.matchedBy === null
                          ? t(`transferMatch.${item.match}` as const)
                          : t('transfers.matchedBy', {
                              by: t(`transferMatchedBy.${item.matchedBy}` as const),
                            })
                      }
                    />
                    {item.problem !== null ? (
                      <Typography variant="caption" color="error" sx={{ display: 'block' }}>
                        {item.problem}
                      </Typography>
                    ) : null}
                  </TableCell>
                  <TableCell>
                    {applied ? (
                      item.outcome !== null ? (
                        <>
                          <Chip
                            size="small"
                            variant="outlined"
                            color={
                              item.outcome === 'FAILED'
                                ? 'error'
                                : item.outcome === 'SKIPPED'
                                  ? 'default'
                                  : 'success'
                            }
                            label={t(`transferOutcome.${item.outcome}` as const)}
                          />
                          {item.outcomeReason !== null ? (
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                              {t(outcomeReasonKey(item.outcomeReason))}
                            </Typography>
                          ) : null}
                          {item.outcomeDetail !== null ? (
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              sx={{ display: 'block', whiteSpace: 'pre-line', wordBreak: 'break-word' }}
                            >
                              {item.outcomeDetail}
                            </Typography>
                          ) : null}
                        </>
                      ) : (
                        t('common.none')
                      )
                    ) : (
                      t(
                        item.match === 'INVALID'
                          ? 'transfers.wouldSkip'
                          : item.match === 'NEW'
                            ? 'transfers.wouldCreate'
                            : (`transferMode.would.${mode}` as const),
                      )
                    )}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
      {!query.isLoading && rows.length === 0 ? <EmptyState title={t('transfers.noItems')} /> : null}
      <ServerPagination
        page={page}
        pageSize={query.data?.pageSize ?? 25}
        total={query.data?.total ?? 0}
        onPageChange={setPage}
      />
    </Paper>
  );
};
